import { useEffect, useState } from "react";
import { api, formatTRY, downloadExport, toNum } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, ArrowUpRight, ArrowDownRight, FileSpreadsheet, Pencil } from "lucide-react";
import { toast } from "sonner";
import { keepScroll } from "@/lib/keepScroll";
import { FinanceDetailDialog } from "@/components/FinanceDetailDialog";

const EXPENSE_CATS = ["Hammadde / Malzeme", "Kira & İşletme", "Personel Maaşları", "Elektrik & Su & Gaz", "Pazarlama & Reklam", "Ekipman & Bakım", "Diğer"];
const INCOME_CATS = ["Satış Dışı Gelir", "Catering", "Abonelik", "Diğer Gelir"];

export default function Finances() {
  const [txs, setTxs] = useState([]);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("expense");
  const [category, setCategory] = useState(EXPENSE_CATS[0]);
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [customCat, setCustomCat] = useState("");
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const isOther = category === "Diğer" || category === "Diğer Gelir";
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const load = () => keepScroll(() => api.get("/transactions").then((r) => setTxs(r.data)));
  useEffect(() => { load(); }, []);

  const openNew = (t) => {
    setEditing(null);
    setType(t); setCategory(t === "expense" ? EXPENSE_CATS[0] : INCOME_CATS[0]);
    setAmount(""); setDesc(""); setCustomCat(""); setDate(new Date().toISOString().slice(0, 10)); setOpen(true);
  };

  const openEdit = (t) => {
    const list = t.type === "expense" ? EXPENSE_CATS : INCOME_CATS;
    const known = list.includes(t.category);
    setEditing(t.id); setType(t.type);
    setCategory(known ? t.category : (t.type === "expense" ? "Diğer" : "Diğer Gelir"));
    setCustomCat(known ? "" : t.category);
    setAmount(String(t.amount)); setDesc(t.description || ""); setDate(t.date); setOpen(true);
  };

  const save = async () => {
    if (!amount || toNum(amount) <= 0) return toast.error("Geçerli bir tutar girin");
    if (isOther && !customCat.trim()) return toast.error("Kategori adı yazın");
    const payload = { type, category: isOther ? customCat.trim() : category, amount: toNum(amount), description: desc, date };
    if (editing) {
      await api.put(`/transactions/${editing}`, payload);
      toast.success("Kayıt güncellendi");
    } else {
      await api.post("/transactions", payload);
      toast.success(type === "income" ? "Gelir eklendi" : "Gider eklendi");
    }
    keepScroll(async () => { setOpen(false); await load(); });
  };

  const del = async (id) => { await api.delete(`/transactions/${id}`); toast.success("Kayıt silindi"); load(); };

  const cafeTxs = txs.filter((t) => t.source !== "club");
  const income = cafeTxs.filter((t) => t.type === "income").reduce((a, t) => a + t.amount, 0);
  const expense = cafeTxs.filter((t) => t.type === "expense").reduce((a, t) => a + t.amount, 0);
  const purchases = cafeTxs.filter((t) => t.type === "expense" && t.category === "Malzeme Alımı").reduce((a, t) => a + t.amount, 0);
  const clubIncome = txs.filter((t) => t.source === "club" && t.type === "income").reduce((a, t) => a + t.amount, 0);
  const clubExpense = txs.filter((t) => t.source === "club" && t.type === "expense").reduce((a, t) => a + t.amount, 0);
  const cats = type === "expense" ? EXPENSE_CATS : INCOME_CATS;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Gelir / Gider Takibi</h1>
          <p className="mt-1 text-sm text-muted-foreground">İşletme gelir ve giderlerinizi kaydedin</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="export-transactions-btn" onClick={async () => {
            try { await downloadExport("/export/transactions", {}, "fithouse_gelir_gider.xlsx"); toast.success("Excel dosyası indirildi"); }
            catch (e) { toast.error("Dışa aktarma başarısız"); }
          }}>
            <FileSpreadsheet className="mr-1 h-4 w-4 text-primary" /> Excel'e Aktar
          </Button>
          <Button data-testid="add-income-btn" variant="secondary" onClick={() => openNew("income")}>
            <ArrowUpRight className="mr-1 h-4 w-4 text-primary" /> Gelir Ekle
          </Button>
          <Button data-testid="add-expense-btn" onClick={() => openNew("expense")}>
            <ArrowDownRight className="mr-1 h-4 w-4" /> Gider Ekle
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div role="button" className="cursor-pointer rounded-xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-primary/50" data-testid="finance-income-card" onClick={() => setDetail({ kind: "tx_income", title: "Gelir Kayıtları (Fit House)" })}>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Toplam Gelir (Fit House)</p>
          <p className="mt-2 font-heading text-2xl font-extrabold text-primary" data-testid="finance-income">{formatTRY(income)}</p>
          <p className="mt-1 text-[10px] font-medium uppercase tracking-wider text-primary/80">Detay için tıklayın</p>
        </div>
        <div role="button" className="cursor-pointer rounded-xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-primary/50" data-testid="finance-expense-card" onClick={() => setDetail({ kind: "tx_expense", title: "Gider Kayıtları (Fit House)" })}>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Toplam Gider (Fit House)</p>
          <p className="mt-2 font-heading text-2xl font-extrabold text-destructive" data-testid="finance-expense">{formatTRY(expense)}</p>
          {purchases > 0 && <p className="mt-1 text-[11px] text-muted-foreground" data-testid="finance-purchases-note">İçinde stok alımı {formatTRY(purchases)} — kâr hesabına ürün maliyeti olarak girer, ayrıca gider sayılmaz</p>}
          <p className="mt-1 text-[10px] font-medium uppercase tracking-wider text-primary/80">Detay için tıklayın</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Fark</p>
          <p className={`mt-2 font-heading text-2xl font-extrabold ${income - expense >= 0 ? "text-primary" : "text-destructive"}`}>{formatTRY(income - expense)}</p>
        </div>
      </div>

      {(clubIncome > 0 || clubExpense > 0) && (
        <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 px-5 py-3 text-sm" data-testid="finance-club">
          <span className="mr-2 text-xs font-semibold uppercase tracking-wider text-primary">Beslenme Kulübü (ayrı kasa)</span>
          Tahsilat <b>{formatTRY(clubIncome)}</b> · Sahip payı <b className="text-destructive">−{formatTRY(clubExpense)}</b> · Net <b className="text-primary">{formatTRY(clubIncome - clubExpense)}</b>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="divide-y divide-border" data-testid="transactions-list">
          {txs.map((t) => (
            <div key={t.id} className="flex items-center justify-between px-5 py-3">
              <div className="flex items-center gap-3">
                <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${t.type === "income" ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`}>
                  {t.type === "income" ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                </div>
                <div>
                  <p className="text-sm font-medium">{t.category}{t.source === "club" && <span className="ml-1.5 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-primary" data-testid={`tx-club-${t.id}`}>Kulüp</span>}</p>
                  <p className="text-xs text-muted-foreground">{t.date}{t.description ? ` · ${t.description}` : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`font-heading font-bold ${t.type === "income" ? "text-primary" : "text-destructive"}`}>
                  {t.type === "income" ? "+" : "−"}{formatTRY(t.amount)}
                </span>
                <Button size="icon" variant="ghost" data-testid={`edit-tx-${t.id}`} onClick={() => openEdit(t)}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" data-testid={`delete-tx-${t.id}`} onClick={() => del(t.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
          {txs.length === 0 && <p className="px-5 py-12 text-center text-sm text-muted-foreground">Henüz kayıt yok</p>}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editing ? (type === "income" ? "Geliri Düzenle" : "Gideri Düzenle") : (type === "income" ? "Gelir Ekle" : "Gider Ekle")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Kategori</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1" data-testid="tx-category"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              {isOther && (
                <Input data-testid="tx-custom-category" autoFocus placeholder="Kategori adını yazın (örn. Temizlik Malzemesi)" value={customCat}
                  onChange={(e) => setCustomCat(e.target.value)} className="mt-2" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tutar ₺</Label>
                <Input data-testid="tx-amount" type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Tarih</Label>
                <Input data-testid="tx-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Açıklama (opsiyonel)</Label>
              <Textarea data-testid="tx-desc" value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-tx-btn" onClick={save} className="font-semibold">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <FinanceDetailDialog detail={detail} onClose={() => setDetail(null)} onChanged={load} />
    </div>
  );
}
